import React, { useState } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import Papa from "papaparse";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import * as XLSX from "xlsx";
import {
  customHeadersForExport,
  teamMembersExportColumnConfig,
} from "../ReactTable/Columns";
import {
  format_arcimedes_modified_Date,
  getCompletedYearsAndMonths,
} from "../../utils";
import client from "../Graphql/client";
import { EXPORT_CONTACTS } from "../Graphql/ContactsQueries/contactsQuery";

const EXPORT_CONTACT_TYPE_MAP = {
  "team-members": "team",
  team: "team",
  firms: "firms",
  agents: "agents",
  vendors: "vendor",
  tenants: "tenant",
  lenders: "lenders",
  investors: "investors",
};

const getExportContactType = (fileName) =>
  EXPORT_CONTACT_TYPE_MAP[fileName] || fileName;

const downloadFileFromBase64 = (fileBase64, filename, mimeType) => {
  const base64Data = fileBase64.includes(",")
    ? fileBase64.split(",")[1]
    : fileBase64;
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Uint8Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const blob = new Blob([byteNumbers], {
    type:
      mimeType ||
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename);
    return;
  }

  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.style.display = "none";
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(downloadUrl);
};

const ExportCRM = ({ exportData, setExportData, printType }) => {
  const [isExporting, setIsExporting] = useState(false);
  const url = location.href;
  const pathParts = url.split("/"); // Split the URL by '/'
  const fileName = pathParts[pathParts.length - 1]; // Get the last part of the path
  const fileNameWithCsv = generateFileName(fileName, "csv");
  const fileNameWithExcel = generateFileName(fileName, "xlsx");

  //   const fileData = exportData.map(obj => {
  //     const { arcimedes_row_id, __typename, contact_id, contact_type, ...filteredObj } = obj; // Remove the unwanted data
  //     return Object.fromEntries(
  //         Object.entries(filteredObj).map(([key, value]) => [
  //             key,  // Keep the original key as it is
  //             Array.isArray(value) && value.length === 1 ? value[0] : value // Convert single-item arrays to strings
  //         ])
  //     );
  // });

  const getHeadersForExport = (fileName) => {
    if (!customHeadersForExport.length || !teamMembersExportColumnConfig.length)
      return []; // Ensure the array is not empty
    if (printType !== "All Fields" && fileName === "team-members") {
      const headersData = teamMembersExportColumnConfig[0];
      return headersData[printType] || [];
    } else {
      const headersData = customHeadersForExport[0]; // Extract the first object from the array
      return headersData[fileName] || []; // Return headers or an empty array if not found
    }
  };
  const headerExport = getHeadersForExport(fileName);

  function generateFileName(fileName, extension) {
    const now = new Date();
    const formattedDate = `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${now.getFullYear()}`;
    const formattedTime = `${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}-${String(now.getSeconds()).padStart(2, "0")}`;

    const baseName = fileName
      ? `iSTRIVE_${fileName.toLowerCase() === "firms" ? "COMPANY" : fileName.toUpperCase()}`
      : "export";
    return `${baseName}_${formattedDate}_${formattedTime}.${extension}`;
  }

  const exportToCSV = () => {
    if (exportData.length > 0 && headerExport.length > 0) {
      // Transform data to match custom columns
      const formattedData = exportData.map((item) => {
        let obj = {};

        headerExport.forEach((col) => {
          if (col.name1 && col.name2) {
            // If both name1 and name2 exist, concatenate them
            obj[col.label] =
              (item[col.name1] ? item[col.name1] : "") +
              (item[col.name2] ? " " + item[col.name2] : "");
          } else if (col.key === "is_agent") {
            // If key is 'is_agent', map values
            if (item[col.key] === "y") {
              obj[col.label] = "Agent";
            } else if (item[col.key] === "h") {
              obj[col.label] = "AthCap - Agent";
            } else if (item[col.key] === "e") {
              obj[col.label] = "AthCap - Employee";
            } else if (item[col.key] === "i") {
              obj[col.label] = "Intern";
            } else if (
              item[col.key] === "n" &&
              printType.toLowerCase() !== "terminated"
            ) {
              obj[col.label] = "Employee";
            } else if (
              printType.toLowerCase() === "terminated" ||
              item[col.key] === null
            ) {
              obj[col.label] = "Employee";
            } else {
              obj[col.label] = item[col.key] != null ? item[col.key] : "";
            }
          } else if (col.key === "cap_rate_min" || col.key === "cap_rate_max") {
            // Append % for cap rate values
            obj[col.label] = item[col.key] != null ? item[col.key] + "%" : "";
          } else if (col.key === "price_min" || col.key === "price_max") {
            // Append $ for price values
            obj[col.label] = item[col.key] != null ? "$" + item[col.key] : "";
          } else if (col.label === "Status") {
            obj[col.label] =
              item[col.key] === null
                ? "Deleted"
                : item[col.key]
                  ? "Terminated"
                  : "";
          } else if (col.label === "DATE") {
            if (item[col.key]) {
              obj[col.label] = item[col.key]; // already formatted → keep as is
            } else if (item[col.key1]) {
              obj[col.label] = format_arcimedes_modified_Date(item[col.key1]);
            } else {
              obj[col.label] = "";
            }
          } else if (col.label === "YRS AT STRIVE") {
            // Case 1 → already computed value available
            if (col.key === "yrs_at_strive") {
              obj[col.label] = item[col.key] || "";
            }
            // Case 2 → need to calculate from dates
            else {
              const start = item[col.key];
              const termination = item[col.key1];
              const arcimedes = item[col.key3];

              if (start && termination) {
                obj[col.label] = getCompletedYearsAndMonths(start, termination);
              } else if (start && arcimedes) {
                const formattedArcimedesDate =
                  format_arcimedes_modified_Date(arcimedes);

                obj[col.label] = getCompletedYearsAndMonths(
                  start,
                  formattedArcimedesDate,
                );
              } else {
                obj[col.label] = "";
              }
            }
          }  else if (col.key === "contact_name") {
  const fullName = (item[col.key] || "").trim();

  // split by one or more spaces
  const parts = fullName.split(/\s+/);

  if (col.label === "FIRST NAME") {
    obj[col.label] = parts[0] || "";
  } 
  else if (col.label === "LAST NAME") {
    // join remaining words as last name
    obj[col.label] = parts.length > 1 ? parts.slice(1).join(" ") : "";
  } 
  else {
    obj[col.label] = "";
  }
}
          
          else if (
            col.key1 &&
            (item[col.key] == null || item[col.key] === "")
          ) {
            // Handle key1 if key is missing
            obj[col.label] = item[col.key1] != null ? item[col.key1] : "";
          } else {
            // Default case: Use key if available
            obj[col.label] = item[col.key] != null ? item[col.key] : "";
          }
        });

        return obj;
      });

      // Convert formatted data to CSV
      const csv = Papa.unparse(formattedData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      // Create and trigger download link
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", fileNameWithCsv);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  const downloadExportFile = async () => {
    const apiType = getExportContactType(fileName);
    const token =
      localStorage.getItem("token") || localStorage.getItem("authToken");

    if (!token) {
      alert("Authentication token not found. Please login.");
      return;
    }

    setIsExporting(true);
    try {
      const { data } = await client.query({
        query: EXPORT_CONTACTS,
        variables: { type: apiType },
        fetchPolicy: "no-cache",
        context: {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      });

      const result = data && data.exportContacts;
      if (!result) {
        throw new Error("Export response was empty.");
      }

      if (!result.success) {
        throw new Error(result.message || "Export failed.");
      }

      if (!result.fileBase64) {
        throw new Error(
          result.message || "Export returned no file data.",
        );
      }

      downloadFileFromBase64(
        result.fileBase64,
        result.filename || fileNameWithExcel,
        result.mimeType,
      );
    } catch (error) {
      console.error("Error downloading export file:", error);
      const message =
        error.graphQLErrors && error.graphQLErrors.length
          ? error.graphQLErrors.map((e) => e.message).join(", ")
          : error.message;
      alert(`Failed to download file: ${message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const exportToExcel = () => {
    if (exportData.length > 0 && headerExport.length > 0) {
      // Transform data to match custom columns
      const formattedData = exportData.map((item) => {
        let obj = {};

        headerExport.forEach((col) => {
          if (col.name1 && col.name2) {
            // If both name1 and name2 exist, concatenate them
            obj[col.label] =
              (item[col.name1] ? item[col.name1] : "") +
              (item[col.name2] ? " " + item[col.name2] : "");
          } else if (col.key === "is_agent") {
            // If key is 'is_agent', map values
            if (item[col.key] === "y") {
              obj[col.label] = "Agent";
            } else if (item[col.key] === "h") {
              obj[col.label] = "AthCap - Agent";
            } else if (item[col.key] === "e") {
              obj[col.label] = "AthCap - Employee";
            } else if (item[col.key] === "i") {
              obj[col.label] = "Intern";
            } else if (
              item[col.key] === "n" &&
              printType.toLowerCase() !== "terminated"
            ) {
              obj[col.label] = "Employee";
            } else if (
              printType.toLowerCase() === "terminated" ||
              item[col.key] === null
            ) {
              obj[col.label] = "Employee";
            } else {
              obj[col.label] = item[col.key] != null ? item[col.key] : "";
            }
          } else if (col.key === "cap_rate_min" || col.key === "cap_rate_max") {
            // Append % for cap rate values
            obj[col.label] = item[col.key] != null ? item[col.key] + "%" : "";
          } else if (col.key === "price_min" || col.key === "price_max") {
            // Append $ for price values
            obj[col.label] = item[col.key] != null ? "$" + item[col.key] : "";
          } else if (col.label === "Status") {
            obj[col.label] =
              item[col.key] === null
                ? "Deleted"
                : item[col.key]
                  ? "Terminated"
                  : "";
          } else if (col.label === "DATE") {
            if (item[col.key]) {
              obj[col.label] = item[col.key]; // already formatted → keep as is
            } else if (item[col.key1]) {
              obj[col.label] = format_arcimedes_modified_Date(item[col.key1]);
            } else {
              obj[col.label] = "";
            }
          } else if (col.label === "YRS AT STRIVE") {
            // Case 1 → already computed value available
            if (col.key === "yrs_at_strive") {
              obj[col.label] = item[col.key] || "";
            }
            // Case 2 → need to calculate from dates
            else {
              const start = item[col.key];
              const termination = item[col.key1];
              const arcimedes = item[col.key3];

              if (start && termination) {
                obj[col.label] = getCompletedYearsAndMonths(start, termination);
              } else if (start && arcimedes) {
                const formattedArcimedesDate =
                  format_arcimedes_modified_Date(arcimedes);

                obj[col.label] = getCompletedYearsAndMonths(
                  start,
                  formattedArcimedesDate,
                );
              } else {
                obj[col.label] = "";
              }
            }
          }
          else if (col.key === "contact_name") {
  const fullName = (item[col.key] || "").trim();

  // split by one or more spaces
  const parts = fullName.split(/\s+/);

  if (col.label === "FIRST NAME") {
    obj[col.label] = parts[0] || "";
  } 
  else if (col.label === "LAST NAME") {
    // join remaining words as last name
    obj[col.label] = parts.length > 1 ? parts.slice(1).join(" ") : "";
  } 
  else {
    obj[col.label] = "";
  }
}

          else if (
            col.key1 &&
            (item[col.key] == null || item[col.key] === "")
          ) {
            // Handle key1 if key is missing
            obj[col.label] = item[col.key1] != null ? item[col.key1] : "";
          } else {
            // Default case: Use key if available
            obj[col.label] = item[col.key] != null ? item[col.key] : "";
          }
        });

        return obj;
      });

      // Convert JSON to worksheet
      const ws = XLSX.utils.json_to_sheet(formattedData);

      // Create a new workbook and append worksheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

      // Export the file
      XLSX.writeFile(wb, fileNameWithExcel);
    }
  };

  return (
    <div>
      <React.Fragment>
        <button
          type="button"
          className={`button-new positive${isExporting ? " inactive" : ""}`}
          style={{ width: "auto", padding: "0 16px" }}
          disabled={isExporting}
          onClick={() => {
            downloadExportFile();
          }}
        >
          {isExporting ? "Exporting..." : "Export"}
        </button>
      </React.Fragment>
      {/* <PopupState variant="popover" popupId="demo-popup-menu">
        {(popupState) => (
          <React.Fragment>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#90C54B",
                // "&:hover": { backgroundColor: "darkgreen" },
              }}
              {...bindTrigger(popupState)}
              disabled={exportData.length == 0}
            >
              Export
              <ArrowDropDownIcon sx={{ ml: 1, color: "inherit" }} />
            </Button>
            {/* <Menu
              {...bindMenu(popupState)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center", // Align horizontally to the center
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center", // Ensure it opens centered
              }}
             
            > */}
      {/* <MenuItem
                onClick={() => {
                  popupState.close();
                  exportToExcel();
                }}
                sx={{
                  marginTop:"2px",
                  width: "118px", 
                  color: "black", // Set text color to white
                  "&:hover": {
                    backgroundColor: "#e3f2fd", // Set hover color to green
                  },
                }}
              >
                Excel
              </MenuItem> */}
      {/* <MenuItem
                onClick={() => {
                  popupState.close();
                  exportToCSV();  // Pass popupState to the exportToCSV function
                }}
                sx={{
                  width: "118px", 
                  color: "black", // Set text color to white
                  "&:hover": {
                    backgroundColor: "#e3f2fd", // Set hover color to green
                  },
                }}
              >
                CSV
              </MenuItem> */}
      {/* </Menu> */}
      {/* </React.Fragment> */}
      {/* )} */}
      {/* </PopupState>  */}
    </div>
  );
};

export default ExportCRM;
